import React, { useState } from 'react';
import { Prerequisites } from '../components/DocBlocks';
import './ContractCreate.css';

const STEPS = [
  { id: 'intent',     label: 'Setup' },
  { id: 'customer',   label: 'Customer' },
  { id: 'components', label: 'Components' },
  { id: 'configure',  label: 'Configure' },
  { id: 'settings',   label: 'Guardrails' },
  { id: 'summary',    label: 'Summary' },
];

// ── Contract profiles ─────────────────────────────────────────────────────────

const PROFILES = [
  {
    id: 'usage',
    icon: '↗',
    label: 'Usage-based',
    tagline: 'Charge based on actual consumption each period',
    detail: 'Customer pays after the period closes. Charges vary with usage — deliveries, API calls, storage, transactions.',
    paymentMode: 'postpaid',
    suggestedFCs: ['Delivery Fee', 'Transaction Fee', 'API Access Fee', 'Storage Fee'],
  },
  {
    id: 'prepaid',
    icon: '⊞',
    label: 'Prepaid usage',
    tagline: 'Customer commits upfront, reconcile actuals later',
    detail: 'Customer sets an estimate and pays at period start. Overages carry forward as a charge; shortfalls as a credit.',
    paymentMode: 'prepaid',
    suggestedFCs: ['Delivery Fee', 'Transaction Fee', 'API Access Fee', 'Storage Fee'],
  },
  {
    id: 'subscription',
    icon: '○',
    label: 'Fixed subscription',
    tagline: 'Same charge every period, no usage tracking',
    detail: 'Flat recurring fee — a monthly platform fee, annual license, or support retainer. No usage measurement needed.',
    paymentMode: 'prepaid',
    suggestedFCs: ['Platform Fee', 'License Fee', 'Subscription Fee', 'Support Fee', 'Setup Fee'],
  },
  {
    id: 'custom',
    icon: '⊕',
    label: 'Custom / Mixed',
    tagline: 'Mix and match billing models per component',
    detail: 'Combine fixed and usage-based components. Some prepaid, some postpaid. Full control over every setting.',
    paymentMode: null,
    suggestedFCs: null,
  },
];

const BILLING_CYCLES  = ['Monthly', 'Quarterly', 'Annual'];
const CURRENCIES      = ['USD', 'EUR', 'GBP', 'INR', 'AED', 'SGD', 'AUD'];
const AVAILABLE_TAXES = ['GST', 'VAT', 'Sales Tax', 'CGST', 'SGST', 'IGST', 'Withholding Tax'];
const PERIOD_MONTHS   = { Monthly: 1, Quarterly: 3, Annual: 12 };

const EXAMPLE_CUSTOMERS = [
  { name: 'Acme Corp',       taxId: 'EIN 45-1234567' },
  { name: 'GlobalTech Ltd',  taxId: 'VAT GB123456789' },
  { name: 'Nexus Retail',    taxId: 'EIN 12-9876543' },
  { name: 'Alpha Logistics', taxId: 'GSTIN 27AABCL5678B2Z3' },
  { name: 'Bluewave Media',  taxId: '' },
  { name: 'Orion Systems',   taxId: 'VAT DE987654321' },
];

const CATALOG_FCS = [
  'Delivery Fee',
  'Platform Fee',
  'Storage Fee',
  'Support Fee',
  'Setup Fee',
  'License Fee',
  'Transaction Fee',
  'API Access Fee',
  'Subscription Fee',
  'Professional Services',
];

const USAGE_ATTRIBUTES = ['Distance (km)', 'Orders', 'API Calls', 'Storage (GB)', 'Users', 'Transactions'];
const TAG_OPTIONS      = ['Zone A', 'Zone B', 'Zone C', 'Tier 1', 'Tier 2', 'Standard', 'Premium'];
const EST_VALUES       = [100, 500, 1000, 5000, 10000, 50000];

const PCT_PRESETS    = [5, 10, 15, 20, 25];
const FIXED_PRESETS  = [100, 250, 500, 1000, 2500];

const DATE_PRESETS = [
  { label: '2025',     from: '2025-01-01', to: '2025-12-31' },
  { label: '2026',     from: '2026-01-01', to: '2026-12-31' },
  { label: 'H1 2025',  from: '2025-01-01', to: '2025-06-30' },
  { label: 'H2 2025',  from: '2025-07-01', to: '2025-12-31' },
  { label: 'H1 2026',  from: '2026-01-01', to: '2026-06-30' },
  { label: 'H2 2026',  from: '2026-07-01', to: '2026-12-31' },
];

const CYCLE_PRESETS = [
  { label: '1–3',  from: '1', to: '3' },
  { label: '1–6',  from: '1', to: '6' },
  { label: '1–12', from: '1', to: '12' },
  { label: '4–6',  from: '4', to: '6' },
  { label: '7–∞',  from: '7', to: '' },
];

function estimateMultiplier(billingCycle, estimatePeriod) {
  const bcMonths  = PERIOD_MONTHS[billingCycle]  ?? 1;
  const estMonths = PERIOD_MONTHS[estimatePeriod] ?? 1;
  return estMonths >= bcMonths ? 1 : bcMonths / estMonths;
}

function subPeriods(billingCycle) {
  const bcMonths = PERIOD_MONTHS[billingCycle] ?? 1;
  return BILLING_CYCLES.filter((p) => (PERIOD_MONTHS[p] ?? 1) <= bcMonths);
}

const BLANK_FC_CONFIG = {
  paymentMode:          'postpaid',
  billingCycle:         'Monthly',
  scheduleMode:         'contract',
  scheduleFrom:         '',
  scheduleTo:           '',
  taxes:                [],
  discounts:            [],
  estimates:            [],
  trueUp:               false,
  trueDown:             false,
  autoAdjustEstimate:   false,
  accumulateUsage:      false,
  accumulationWindow:   3,
};

function buildDraft() {
  return {
    profile:          null,
    billingCycle:     'Monthly',   // intent-level default applied to all FCs
    customerName:     '',
    customerTaxId:    '',
    currency:         'USD',
    feeComponents:    [],
    fcConfigs:        {},
    autoRenew:        true,
    contractDiscount: null,
    bpMin: '', bpMax: '', bpIncluded: [],
    ctMin: '', ctMax: '',
    virtualStartDate: '',
  };
}

function profileDefaultConfig(profile, billingCycle) {
  const paymentMode = PROFILES.find((p) => p.id === profile)?.paymentMode || 'postpaid';
  return { ...BLANK_FC_CONFIG, paymentMode, billingCycle: billingCycle || 'Monthly' };
}

function fmtVal(v, currency) {
  if (v === '' || v == null) return '—';
  return `${currency} ${Number(v).toLocaleString()}`;
}

// ── Story primitives ──────────────────────────────────────────────────────────

function StoryNote({ children }) {
  return <p className="ccf-story-note">{children}</p>;
}

function WhatIf({ children }) {
  return (
    <div className="ccf-whatif">
      <span className="ccf-whatif-label">Try it</span>
      {children}
    </div>
  );
}

function ReactiveNote({ children }) {
  return (
    <div className="ccf-reactive">
      <span className="ccf-reactive-label">What changed</span>
      {children}
    </div>
  );
}

function SegBtn({ active, onClick, children }) {
  return (
    <button className={`ccf-seg-btn${active ? ' ccf-seg-btn--on' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

// ── Step indicator ────────────────────────────────────────────────────────────

function StepIndicator({ current }) {
  return (
    <div className="ccf-stepper">
      {STEPS.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'active' : 'pending';
        return (
          <React.Fragment key={s.id}>
            <div className={`ccf-step-item ccf-step-item--${state}`}>
              <div className="ccf-step-circle">{state === 'done' ? '✓' : i + 1}</div>
              <span className="ccf-step-label">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`ccf-step-connector${i < current ? ' ccf-step-connector--done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Step 1: Intent ────────────────────────────────────────────────────────────

function StepIntent({ draft, onChange }) {
  const selected = PROFILES.find((p) => p.id === draft.profile);

  return (
    <div className="ccf-step">
      <div className="ccf-step-head">
        <div className="ccf-step-eyebrow">Step 1 — Setup</div>
        <h2 className="ccf-step-title">What kind of contract are you setting up?</h2>
        <StoryNote>
          Your answer shapes what you'll need to configure. Different billing models
          need different settings — pick the one that best describes how this customer gets charged.
        </StoryNote>
      </div>

      <div className="ccf-intent-grid">
        {PROFILES.map((p) => (
          <button
            key={p.id}
            className={`ccf-intent-card${draft.profile === p.id ? ' ccf-intent-card--on' : ''}`}
            onClick={() => onChange('profile', p.id)}
          >
            <span className="ccf-intent-icon">{p.icon}</span>
            <span className="ccf-intent-label">{p.label}</span>
            <span className="ccf-intent-tagline">{p.tagline}</span>
            <span className="ccf-intent-detail">{p.detail}</span>
          </button>
        ))}
      </div>

      {selected && (
        <div className="ccf-intent-followup">
          <label className="ccf-config-label">How often will you invoice?</label>
          <div className="ccf-seg">
            {BILLING_CYCLES.map((c) => (
              <SegBtn key={c} active={draft.billingCycle === c} onClick={() => onChange('billingCycle', c)}>{c}</SegBtn>
            ))}
          </div>
          <p className="ccf-field-caption">
            This becomes the default billing cycle for all fees. You can override it
            per component in the Configure step.
          </p>
          {selected.id !== 'custom' && (
            <ReactiveNote>
              {selected.id === 'usage' && 'All components will default to postpaid — charges are calculated from actual usage at the end of each period.'}
              {selected.id === 'prepaid' && 'All components will default to prepaid — the customer commits to an estimate upfront, reconciled at period end.'}
              {selected.id === 'subscription' && 'All components will default to prepaid with a fixed charge — no usage tracking required.'}
            </ReactiveNote>
          )}
        </div>
      )}
    </div>
  );
}

// ── Step 2: Customer ──────────────────────────────────────────────────────────

function StepCustomer({ draft, onChange }) {
  return (
    <div className="ccf-step">
      <div className="ccf-step-head">
        <div className="ccf-step-eyebrow">Step 2 — Customer</div>
        <h2 className="ccf-step-title">Who is this contract for?</h2>
        <StoryNote>
          The customer is the legal entity being billed. Their name and currency appear on every
          invoice generated under this contract. The tax ID is used for compliance and appears
          on tax invoices.
        </StoryNote>
      </div>
      <div className="ccf-card-list">
        <div className="ccf-config-field">
          <label className="ccf-config-label">Select customer</label>
          <div className="ccf-customer-grid">
            {EXAMPLE_CUSTOMERS.map((c) => (
              <button
                key={c.name}
                className={`ccf-customer-card${draft.customerName === c.name ? ' ccf-customer-card--on' : ''}`}
                onClick={() => { onChange('customerName', c.name); onChange('customerTaxId', c.taxId); }}
              >
                <div className="ccf-customer-name">{c.name}</div>
                {c.taxId
                  ? <div className="ccf-customer-taxid">{c.taxId}</div>
                  : <div className="ccf-customer-taxid ccf-customer-taxid--none">No tax ID</div>
                }
              </button>
            ))}
          </div>
        </div>
        {draft.customerName && (
          <div className="ccf-config-field">
            <label className="ccf-config-label">Billing currency</label>
            <div className="ccf-seg">
              {CURRENCIES.map((c) => (
                <SegBtn key={c} active={draft.currency === c} onClick={() => onChange('currency', c)}>{c}</SegBtn>
              ))}
            </div>
            <p className="ccf-field-caption">
              All invoices under this contract are issued in this currency. The rate card must
              have prices defined in the same currency.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Step 3: Fee components ────────────────────────────────────────────────────

function StepFeeComponents({ feeComponents, onChange, profile }) {
  const toggle = (fc) => {
    const next = feeComponents.includes(fc)
      ? feeComponents.filter((f) => f !== fc)
      : [...feeComponents, fc];
    onChange('feeComponents', next);
  };

  const profileDef  = PROFILES.find((p) => p.id === profile);
  const suggested   = profileDef?.suggestedFCs || [];
  const others      = CATALOG_FCS.filter((fc) => !suggested.includes(fc));
  const showSections = suggested.length > 0;

  function FCItem({ fc }) {
    const on = feeComponents.includes(fc);
    return (
      <div className={`ccf-fc-check${on ? ' ccf-fc-check--on' : ''}`} onClick={() => toggle(fc)}>
        <div className="ccf-checkbox">{on && '✓'}</div>
        <span className="ccf-fc-check-name">{fc}</span>
      </div>
    );
  }

  return (
    <div className="ccf-step">
      <div className="ccf-step-head">
        <div className="ccf-step-eyebrow">Step 3 — Fees</div>
        <h2 className="ccf-step-title">What services are being billed?</h2>
        <StoryNote>
          Each fee becomes a separate line item on the invoice.
          {showSections
            ? ` We've highlighted the components most common for ${profileDef.label} contracts.`
            : ' Components in the same contract can have completely different billing cycles and payment modes.'}
        </StoryNote>
      </div>

      {showSections ? (
        <>
          <div className="ccf-fc-category">
            <span className="ccf-fc-category-label">Typical for {profileDef.label}</span>
            <div className="ccf-fc-checklist">
              {suggested.map((fc) => <FCItem key={fc} fc={fc} />)}
            </div>
          </div>
          <div className="ccf-fc-category ccf-fc-category--other">
            <span className="ccf-fc-category-label">Other options</span>
            <div className="ccf-fc-checklist">
              {others.map((fc) => <FCItem key={fc} fc={fc} />)}
            </div>
          </div>
        </>
      ) : (
        <div className="ccf-fc-checklist">
          {CATALOG_FCS.map((fc) => <FCItem key={fc} fc={fc} />)}
        </div>
      )}

      {feeComponents.length > 1 && profile === 'custom' && (
        <div style={{ padding: '0 24px 20px' }}>
          <WhatIf>
            You have {feeComponents.length} components. In the next step, each gets configured
            independently — try giving them different billing cycles to see how mixed schedules work.
          </WhatIf>
        </div>
      )}
    </div>
  );
}

// ── Step 4: Configure ─────────────────────────────────────────────────────────

function DiscountBands({ bands, onChange, currency }) {
  const addBand = () => {
    const last    = bands[bands.length - 1];
    const newFrom = last ? (last.toPeriod ? last.toPeriod + 1 : null) : 1;
    if (newFrom === null) return;
    onChange([...bands, { fromPeriod: newFrom, toPeriod: null, type: 'percentage', value: 10 }]);
  };
  const removeBand  = (i) => onChange(bands.filter((_, idx) => idx !== i));
  const updateBand  = (i, key, val) =>
    onChange(bands.map((b, idx) => idx === i ? { ...b, [key]: val } : b));

  return (
    <div className="ccf-bands">
      {bands.length === 0 && (
        <span className="ccf-bands-empty">No discount — rate card charge used as-is.</span>
      )}
      {bands.map((b, i) => {
        const isLast = i === bands.length - 1;
        return (
          <div key={i} className="ccf-band-card">
            <div className="ccf-band-card-head">
              <span className="ccf-band-period">Period {b.fromPeriod}–{b.toPeriod ?? '∞'}</span>
              <button className="ccf-band-remove" onClick={() => removeBand(i)}>×</button>
            </div>
            <div className="ccf-band-card-body">
              <div className="ccf-band-field">
                <span className="ccf-band-field-label">Type</span>
                <div className="ccf-seg">
                  <SegBtn active={b.type === 'percentage'} onClick={() => updateBand(i, 'type', 'percentage')}>%</SegBtn>
                  <SegBtn active={b.type === 'fixed'}      onClick={() => updateBand(i, 'type', 'fixed')}>Fixed</SegBtn>
                </div>
              </div>
              <div className="ccf-band-field">
                <span className="ccf-band-field-label">Value</span>
                <div className="ccf-seg" style={{ flexWrap: 'wrap' }}>
                  {b.type === 'percentage'
                    ? PCT_PRESETS.map((v) => (
                        <SegBtn key={v} active={b.value === v} onClick={() => updateBand(i, 'value', v)}>{v}%</SegBtn>
                      ))
                    : FIXED_PRESETS.map((v) => (
                        <SegBtn key={v} active={b.value === v} onClick={() => updateBand(i, 'value', v)}>{currency} {v}</SegBtn>
                      ))
                  }
                </div>
              </div>
              <div className="ccf-band-field">
                <span className="ccf-band-field-label">Ends after period</span>
                <div className="ccf-seg">
                  {[3, 6, 12].map((p) => (
                    <SegBtn key={p} active={b.toPeriod === p} onClick={() => updateBand(i, 'toPeriod', p)}>Pd {p}</SegBtn>
                  ))}
                  {isLast && (
                    <SegBtn active={b.toPeriod === null} onClick={() => updateBand(i, 'toPeriod', null)}>Open-ended</SegBtn>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <button
        className="ccf-add-band"
        onClick={addBand}
        disabled={bands.length > 0 && bands[bands.length - 1].toPeriod === null}
      >
        + Add discount band
      </button>
    </div>
  );
}

function EstimateCard({ estimate, billingCycle, index, onUpdate, onRemove }) {
  const estPeriod = estimate.period || billingCycle;
  const mult      = estimateMultiplier(billingCycle, estPeriod);
  const periods   = subPeriods(billingCycle);
  const set = (key, val) => onUpdate(index, key, val);

  return (
    <div className="ccf-est-card">
      <div className="ccf-est-card-head">
        <span className="ccf-est-card-title">Estimate {index + 1}</span>
        <button className="ccf-band-remove" onClick={() => onRemove(index)}>×</button>
      </div>
      <div className="ccf-est-card-body">
        <div className="ccf-est-field">
          <span className="ccf-est-field-label">Meter</span>
          <div className="ccf-seg" style={{ flexWrap: 'wrap' }}>
            {USAGE_ATTRIBUTES.map((ua) => (
              <SegBtn key={ua} active={estimate.usageAttribute === ua} onClick={() => set('usageAttribute', ua)}>{ua}</SegBtn>
            ))}
          </div>
        </div>
        <div className="ccf-est-field">
          <span className="ccf-est-field-label">Tag</span>
          <div className="ccf-seg" style={{ flexWrap: 'wrap' }}>
            <SegBtn active={!estimate.tags} onClick={() => set('tags', '')}>All tags</SegBtn>
            {TAG_OPTIONS.map((t) => (
              <SegBtn key={t} active={estimate.tags === t} onClick={() => set('tags', t)}>{t}</SegBtn>
            ))}
          </div>
        </div>
        <div className="ccf-est-field">
          <span className="ccf-est-field-label">Estimate period</span>
          <div className="ccf-seg">
            {periods.map((p) => (
              <SegBtn key={p} active={estPeriod === p} onClick={() => set('period', p)}>{p}</SegBtn>
            ))}
          </div>
        </div>
        <div className="ccf-est-field">
          <span className="ccf-est-field-label">Estimated usage</span>
          <div className="ccf-seg" style={{ flexWrap: 'wrap' }}>
            {EST_VALUES.map((v) => (
              <SegBtn key={v} active={Number(estimate.value) === v} onClick={() => set('value', String(v))}>
                {v.toLocaleString()}
              </SegBtn>
            ))}
          </div>
          {mult > 1 && (
            <div className="ccf-est-mult">×{mult} → {billingCycle.toLowerCase()} charge</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FcConfigCard({ fc, config, onChange, draft, mode, profile }) {
  const set       = (key, val) => onChange(fc, key, val);
  const isPrepaid = config.paymentMode === 'prepaid';
  const currency  = draft?.currency || 'USD';
  const advanced  = mode === 'advanced';

  // When profile locks payment mode, hide the toggle and show a locked badge instead
  const profileDef    = PROFILES.find((p) => p.id === profile);
  const lockedMode    = profile && profile !== 'custom' ? profileDef?.paymentMode : null;
  const showModeToggle = !lockedMode;

  const selectedDatePreset  = DATE_PRESETS.find(
    (p) => p.from === config.scheduleFrom && p.to === config.scheduleTo
  );
  const selectedCyclePreset = CYCLE_PRESETS.find(
    (p) => p.from === config.scheduleFrom && p.to === config.scheduleTo
  );

  return (
    <div className="ccf-config-card">
      <div className="ccf-config-header">
        <span className="ccf-config-fc-name">{fc}</span>
      </div>

      {/* Payment mode */}
      <div className="ccf-config-field">
        <label className="ccf-config-label">Payment mode</label>
        {showModeToggle ? (
          <>
            {advanced && (
              <p className="ccf-field-caption">
                Postpaid: charged at period end based on actual usage.
                Prepaid: customer commits to an estimate upfront — reconciled at period end.
              </p>
            )}
            <div className="ccf-seg">
              <SegBtn active={config.paymentMode === 'postpaid'} onClick={() => set('paymentMode', 'postpaid')}>Postpaid</SegBtn>
              <SegBtn active={config.paymentMode === 'prepaid'}  onClick={() => set('paymentMode', 'prepaid')}>Prepaid</SegBtn>
            </div>
            {isPrepaid && (
              <ReactiveNote>
                Switched to prepaid. The customer commits to an estimated usage amount at the start
                of each period. Add estimates below — the pricing model runs on them immediately
                rather than waiting for actual usage.
              </ReactiveNote>
            )}
          </>
        ) : (
          <div className="ccf-mode-locked">
            <span className={`ccf-mode-locked-badge ccf-mode-locked-badge--${lockedMode}`}>{lockedMode}</span>
            <span className="ccf-mode-locked-reason">Set by contract type · change in Step 1 to override</span>
          </div>
        )}
      </div>

      {/* Reconciliation — prepaid only, advanced */}
      {isPrepaid && advanced && (
        <div className="ccf-config-field">
          <label className="ccf-config-label">Reconciliation</label>
          <p className="ccf-field-caption">
            At the end of each billing period, actual usage is compared against the estimate.
            These settings control what happens when they don't match.
          </p>

          <div className="ccf-reconcile-rows">
            <div className="ccf-reconcile-row">
              <div className="ccf-reconcile-info">
                <span className="ccf-reconcile-name">True Up</span>
                <span className="ccf-reconcile-desc">
                  Actual &gt; Estimate — the cost of the extra usage carries over as a charge on the next invoice.
                </span>
              </div>
              <div className="ccf-seg ccf-seg--sm">
                <SegBtn active={config.trueUp}  onClick={() => set('trueUp', true)}>On</SegBtn>
                <SegBtn active={!config.trueUp} onClick={() => set('trueUp', false)}>Off</SegBtn>
              </div>
            </div>

            <div className="ccf-reconcile-row">
              <div className="ccf-reconcile-info">
                <span className="ccf-reconcile-name">True Down</span>
                <span className="ccf-reconcile-desc">
                  Actual &lt; Estimate — the unused portion is credited against the next invoice.
                </span>
              </div>
              <div className="ccf-seg ccf-seg--sm">
                <SegBtn active={config.trueDown}  onClick={() => set('trueDown', true)}>On</SegBtn>
                <SegBtn active={!config.trueDown} onClick={() => set('trueDown', false)}>Off</SegBtn>
              </div>
            </div>

            <div className="ccf-reconcile-row">
              <div className="ccf-reconcile-info">
                <span className="ccf-reconcile-name">Auto-adjust estimate</span>
                <span className="ccf-reconcile-desc">
                  Next period's estimate is automatically replaced by the previous period's actual usage.
                </span>
              </div>
              <div className="ccf-seg ccf-seg--sm">
                <SegBtn active={config.autoAdjustEstimate}  onClick={() => set('autoAdjustEstimate', true)}>On</SegBtn>
                <SegBtn active={!config.autoAdjustEstimate} onClick={() => set('autoAdjustEstimate', false)}>Off</SegBtn>
              </div>
            </div>
          </div>

          {(config.trueUp || config.trueDown) && (
            <ReactiveNote>
              {config.trueUp && config.trueDown && (
                <>Both directions are active — overages are charged on the next invoice, shortfalls are credited. The customer always pays for exactly what they used.</>
              )}
              {config.trueUp && !config.trueDown && (
                <>True Up only — if actual exceeds the estimate, the extra cost is billed next period. If actual comes in under, the customer keeps the savings and pays only the estimate.</>
              )}
              {!config.trueUp && config.trueDown && (
                <>True Down only — if actual comes in under, the unused amount is credited next period. If actual exceeds the estimate, the overage is absorbed and not billed.</>
              )}
            </ReactiveNote>
          )}
          {config.autoAdjustEstimate && (
            <ReactiveNote>
              Auto-adjust is on. After each period closes, the billing engine replaces the next
              period's estimate with the actual usage just recorded — no manual update needed.
              {config.trueUp && ' Combined with True Up, any carry-over from this period is added on top of the adjusted estimate.'}
            </ReactiveNote>
          )}
        </div>
      )}

      {/* Billing cycle */}
      <div className="ccf-config-field">
        <label className="ccf-config-label">Billing cycle</label>
        <div className="ccf-seg">
          {BILLING_CYCLES.map((c) => (
            <SegBtn key={c} active={config.billingCycle === c} onClick={() => set('billingCycle', c)}>{c}</SegBtn>
          ))}
        </div>
        {advanced && (
          <p className="ccf-field-caption">
            Each component invoices on its own cycle — two components in the same contract
            can bill on different schedules and generate separate invoices independently.
          </p>
        )}
      </div>

      {/* Usage accumulation — postpaid only, advanced */}
      {!isPrepaid && advanced && (
        <div className="ccf-config-field">
          <label className="ccf-config-label">Usage accumulation</label>
          <p className="ccf-field-caption">
            When on, usage is summed across multiple billing periods before the pricing model runs.
            This means <strong>cost(sum)</strong> rather than <strong>sum of cost(period)</strong> —
            the difference matters when the pricing model has tiers.
          </p>
          <div className="ccf-seg">
            <SegBtn active={!config.accumulateUsage} onClick={() => set('accumulateUsage', false)}>Off</SegBtn>
            <SegBtn active={config.accumulateUsage}  onClick={() => set('accumulateUsage', true)}>On</SegBtn>
          </div>
          {config.accumulateUsage && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                <span className="ccf-config-label" style={{ margin: 0 }}>Accumulate over</span>
                <div className="ccf-seg">
                  {[2, 3, 4, 6, 12].map((n) => (
                    <SegBtn
                      key={n}
                      active={config.accumulationWindow === n}
                      onClick={() => set('accumulationWindow', n)}
                    >
                      {n} periods
                    </SegBtn>
                  ))}
                </div>
              </div>
              <ReactiveNote>
                Usage is pooled across {config.accumulationWindow} billing periods before pricing runs.
                For example, if usage is 100, 110, and 90 across three periods, the charge is{' '}
                <strong>cost(300)</strong> — not cost(100) + cost(110) + cost(90).
                On a graduated pricing model this typically results in a lower total bill
                because more volume falls into cheaper tiers.
              </ReactiveNote>
            </>
          )}
        </div>
      )}

      {/* Active period */}
      <div className="ccf-config-field">
        <label className="ccf-config-label">Active period</label>
        <div className="ccf-seg">
          <SegBtn active={config.scheduleMode === 'contract'} onClick={() => set('scheduleMode', 'contract')}>Full contract</SegBtn>
          <SegBtn active={config.scheduleMode === 'date'}     onClick={() => set('scheduleMode', 'date')}>Exact dates</SegBtn>
          <SegBtn active={config.scheduleMode === 'cycle'}    onClick={() => set('scheduleMode', 'cycle')}>By cycle</SegBtn>
        </div>
        {config.scheduleMode === 'date' && (
          <>
            <div className="ccf-seg" style={{ flexWrap: 'wrap', marginTop: 8 }}>
              {DATE_PRESETS.map((p) => (
                <SegBtn
                  key={p.label}
                  active={selectedDatePreset?.label === p.label}
                  onClick={() => { set('scheduleFrom', p.from); set('scheduleTo', p.to); }}
                >
                  {p.label}
                </SegBtn>
              ))}
            </div>
            {selectedDatePreset && (
              <ReactiveNote>
                This component is active {selectedDatePreset.from} → {selectedDatePreset.to}.
                Usage events outside this window are ignored for this component.
              </ReactiveNote>
            )}
          </>
        )}
        {config.scheduleMode === 'cycle' && (
          <>
            <div className="ccf-seg" style={{ flexWrap: 'wrap', marginTop: 8 }}>
              {CYCLE_PRESETS.map((p) => (
                <SegBtn
                  key={p.label}
                  active={selectedCyclePreset?.label === p.label}
                  onClick={() => { set('scheduleFrom', p.from); set('scheduleTo', p.to); }}
                >
                  {p.label}
                </SegBtn>
              ))}
            </div>
            {selectedCyclePreset && (
              <ReactiveNote>
                Cycle numbers resolve to calendar dates once the contract has a start date.
                Cycle 1 = the first full billing period after activation.
              </ReactiveNote>
            )}
          </>
        )}
        <p className="ccf-field-caption">
          {config.scheduleMode === 'contract' && 'Active for the full contract duration.'}
          {config.scheduleMode === 'date'     && 'Select a date range above.'}
          {config.scheduleMode === 'cycle'    && 'Select a cycle range above.'}
        </p>
      </div>

      {/* Taxes */}
      <div className="ccf-config-field">
        <label className="ccf-config-label">Taxes</label>
        <div className="ccf-tax-checks">
          {AVAILABLE_TAXES.map((t) => {
            const on = config.taxes.includes(t);
            return (
              <label key={t} className={`ccf-tax-check${on ? ' ccf-tax-check--on' : ''}`}>
                <input type="checkbox" checked={on} onChange={() =>
                  set('taxes', on ? config.taxes.filter((x) => x !== t) : [...config.taxes, t])
                } />
                {t}
              </label>
            );
          })}
        </div>
        <p className="ccf-field-caption">
          Select which taxes apply to this customer for this component.
        </p>
        {config.taxes.length === 0 && (
          <ReactiveNote>
            No taxes selected — this component is tax-exempt for this customer.
            Make sure you have a valid exemption certificate on file before invoicing.
          </ReactiveNote>
        )}
      </div>

      {/* Prepaid estimates */}
      {isPrepaid && (
        <div className="ccf-config-field">
          <label className="ccf-config-label">Prepaid estimates</label>
          <p className="ccf-field-caption">
            One card per meter and tag combination. You can estimate at a shorter
            interval than the billing cycle — the invoice totals them up.
          </p>
          {config.estimates.map((e, i) => (
            <EstimateCard
              key={i}
              estimate={e}
              billingCycle={config.billingCycle}
              index={i}
              onUpdate={(idx, key, val) =>
                set('estimates', config.estimates.map((x, j) => j === idx ? { ...x, [key]: val } : x))
              }
              onRemove={(idx) => set('estimates', config.estimates.filter((_, j) => j !== idx))}
            />
          ))}
          {config.estimates.length === 0 && (
            <p style={{ color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: 12 }}>
              No estimates yet — add one below.
            </p>
          )}
          <button className="ccf-add-band" style={{ marginTop: 8 }} onClick={() =>
            set('estimates', [...config.estimates, { usageAttribute: '', tags: '', value: '', period: config.billingCycle }])
          }>+ Add estimate</button>
          {config.estimates.some((e) => estimateMultiplier(config.billingCycle, e.period || config.billingCycle) > 1) && (() => {
            const ex   = config.estimates.find((e) => estimateMultiplier(config.billingCycle, e.period || config.billingCycle) > 1);
            const mult = estimateMultiplier(config.billingCycle, ex.period || config.billingCycle);
            const ep   = (ex.period || config.billingCycle).toLowerCase();
            return (
              <ReactiveNote>
                Each estimate covers one {ep}. The pricing model runs {mult} times — once per {ep} —
                and the resulting costs are added together. So if usage is 100 per {ep}, the invoice
                is cost(100) × {mult}, not cost(100 × {mult}). This matters when the pricing model has tiers.
              </ReactiveNote>
            );
          })()}
        </div>
      )}

      {/* Discounts */}
      <div className="ccf-config-field">
        <label className="ccf-config-label">Discount schedule</label>
        <p className="ccf-field-caption">
          Applied after the pricing model runs. Each band covers a range of billing periods.
        </p>
        <DiscountBands bands={config.discounts} onChange={(val) => set('discounts', val)} currency={currency} />
        {config.discounts.length === 1 && (
          <ReactiveNote>
            One open-ended band — this discount applies for the entire contract. Add a second
            band if the discount should step down after a certain number of periods.
          </ReactiveNote>
        )}
        {config.discounts.length > 1 && (() => {
          const first       = config.discounts[0];
          const last        = config.discounts[config.discounts.length - 1];
          const typeChanged = config.discounts.some((b, i) => i > 0 && b.type !== config.discounts[i - 1].type);
          return (
            <ReactiveNote>
              {config.discounts.length} bands.{' '}
              {typeChanged
                ? `The discount type changes — ${first.type} early on, switching to ${last.type} later.`
                : `All bands use ${first.type} discounts, stepping ${Number(last.value) < Number(first.value) ? 'down' : 'up'} over time.`
              }{' '}
              The last band is open-ended and stays in effect until the contract ends.
            </ReactiveNote>
          );
        })()}
      </div>
    </div>
  );
}

function StepConfigure({ draft, onChange, mode, profile }) {
  const setFcConfig = (fc, key, val) =>
    onChange('fcConfigs', {
      ...draft.fcConfigs,
      [fc]: { ...draft.fcConfigs[fc], [key]: val },
    });

  return (
    <div className="ccf-step">
      <div className="ccf-step-head">
        <div className="ccf-step-eyebrow">Step 4 — Configure</div>
        <h2 className="ccf-step-title">How does each service get billed?</h2>
        {mode === 'advanced' && (
          <StoryNote>
            Each component is configured independently. Two components in the same contract
            can have completely different cycles, payment modes, and discount schedules —
            they generate separate invoices on their own timelines.
          </StoryNote>
        )}
      </div>
      <div className="ccf-config-list">
        {draft.feeComponents.map((fc) => (
          <FcConfigCard
            key={fc}
            fc={fc}
            config={draft.fcConfigs[fc] || { ...BLANK_FC_CONFIG }}
            onChange={setFcConfig}
            draft={draft}
            mode={mode}
            profile={profile}
          />
        ))}
      </div>
    </div>
  );
}

// ── Step 4: Guardrails ────────────────────────────────────────────────────────


function StepSettings({ draft, onChange, mode }) {
  const currency    = draft.currency;
  const setDiscount = (key, val) =>
    onChange('contractDiscount', {
      ...(draft.contractDiscount || { type: 'percentage', value: 10, timing: 'pre-tax' }),
      [key]: val,
    });

  const discountTiming = draft.contractDiscount?.timing;
  const hasBpMin       = draft.bpMin !== '' && draft.bpMin != null;
  const hasBpMax       = draft.bpMax !== '' && draft.bpMax != null;
  const hasCtMin       = draft.ctMin !== '' && draft.ctMin != null;
  const hasCtMax       = draft.ctMax !== '' && draft.ctMax != null;
  const validFCs       = draft.feeComponents;
  const partialBpIncl  = validFCs.length > 0 && draft.bpIncluded.length > 0 && draft.bpIncluded.length < validFCs.length;
  const hasDiscount    = draft.contractDiscount != null;

  return (
    <div className="ccf-step">
      <div className="ccf-step-head">
        <div className="ccf-step-eyebrow">Step 5 — Guardrails</div>
        <h2 className="ccf-step-title">What keeps both sides protected?</h2>
        <StoryNote>
          Contract-level settings apply across all components and all billing periods.
          Guardrails give both sides predictability — a minimum protects your revenue,
          a maximum protects the customer.
        </StoryNote>
      </div>

      {/* Virtual start date — advanced */}
      {mode === 'advanced' && <div className="ccf-settings-section">
        <div className="ccf-settings-label">Virtual start date</div>
        <p className="ccf-field-caption">
          Normally, billing periods are anchored to the contract's actual activation date.
          A virtual start date shifts the anchor earlier, so the first period ends at a
          natural calendar boundary instead of repeating the activation day each month.
        </p>
        <div className="ccf-seg">
          <SegBtn active={!draft.virtualStartDate} onClick={() => onChange('virtualStartDate', '')}>None</SegBtn>
          <SegBtn active={!!draft.virtualStartDate} onClick={() => !draft.virtualStartDate && onChange('virtualStartDate', '2025-10-01')}>Set date</SegBtn>
        </div>
        {!!draft.virtualStartDate && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="ccf-settings-sub">Date</span>
              <input
                className="ccf-input-sm"
                type="date"
                value={draft.virtualStartDate}
                onChange={(e) => onChange('virtualStartDate', e.target.value)}
              />
            </div>
            <div className="ccf-vsd-scenario">
              <div className="ccf-vsd-scenario-label">How periods align</div>
              <div className="ccf-vsd-row ccf-vsd-row--before">
                <span className="ccf-vsd-tag">Without</span>
                <span className="ccf-vsd-periods">
                  <span className="ccf-vsd-period ccf-vsd-period--short">Oct 15 – Nov 14</span>
                  <span className="ccf-vsd-arrow">→</span>
                  <span className="ccf-vsd-period">Nov 15 – Dec 14</span>
                  <span className="ccf-vsd-arrow">→</span>
                  <span className="ccf-vsd-period">Dec 15 – Jan 14</span>
                </span>
              </div>
              <div className="ccf-vsd-row ccf-vsd-row--after">
                <span className="ccf-vsd-tag ccf-vsd-tag--on">With</span>
                <span className="ccf-vsd-periods">
                  <span className="ccf-vsd-period ccf-vsd-period--short">Oct 15 – Oct 31</span>
                  <span className="ccf-vsd-arrow">→</span>
                  <span className="ccf-vsd-period ccf-vsd-period--full">Nov 1 – Nov 30</span>
                  <span className="ccf-vsd-arrow">→</span>
                  <span className="ccf-vsd-period ccf-vsd-period--full">Dec 1 – Dec 31</span>
                </span>
              </div>
              <p className="ccf-vsd-note">
                Virtual start: Oct 1 · Actual activation: Oct 15 · Billing cycle: Monthly
              </p>
            </div>
            <ReactiveNote>
              The first period is a short stub — Oct 15 to Oct 31. From November onwards,
              every period runs on a clean calendar month. The virtual start date is never
              billed; it only sets where the period boundaries fall.
            </ReactiveNote>
          </>
        )}
      </div>}

      {/* Auto-renew */}
      <div className="ccf-settings-section">
        <div className="ccf-settings-label">Auto-renew</div>
        <div className="ccf-seg">
          <SegBtn active={draft.autoRenew}  onClick={() => onChange('autoRenew', true)}>Enabled</SegBtn>
          <SegBtn active={!draft.autoRenew} onClick={() => onChange('autoRenew', false)}>Disabled</SegBtn>
        </div>
        <p className="ccf-field-caption">
          When enabled, the contract renews automatically at expiry and a reminder email
          goes to the customer. When disabled, billing stops at the end date.
        </p>
        {!draft.autoRenew && (
          <ReactiveNote>
            Auto-renew is off. The contract expires at its end date — someone must manually
            create a new term before then, or billing stops and usage events are no longer processed.
          </ReactiveNote>
        )}
      </div>

      {/* Contract discount */}
      <div className="ccf-settings-section">
        <div className="ccf-settings-label">Contract discount</div>
        <p className="ccf-field-caption">
          A single flat discount on the total invoice, applied on top of any per-component discounts.
          Unlike component discounts, this doesn't change over time.
        </p>
        <div className="ccf-seg">
          <SegBtn active={!hasDiscount}                                                    onClick={() => onChange('contractDiscount', null)}>None</SegBtn>
          <SegBtn active={hasDiscount && draft.contractDiscount?.type === 'percentage'}    onClick={() => setDiscount('type', 'percentage')}>Percentage</SegBtn>
          <SegBtn active={hasDiscount && draft.contractDiscount?.type === 'fixed'}         onClick={() => setDiscount('type', 'fixed')}>Fixed amount</SegBtn>
        </div>
        {hasDiscount && (
          <>
            <div>
              <div className="ccf-settings-sub">Value</div>
              <div className="ccf-seg" style={{ flexWrap: 'wrap', marginTop: 6 }}>
                {draft.contractDiscount?.type === 'percentage'
                  ? PCT_PRESETS.map((v) => (
                      <SegBtn key={v} active={draft.contractDiscount?.value === v} onClick={() => setDiscount('value', v)}>{v}%</SegBtn>
                    ))
                  : FIXED_PRESETS.map((v) => (
                      <SegBtn key={v} active={draft.contractDiscount?.value === v} onClick={() => setDiscount('value', v)}>{currency} {v}</SegBtn>
                    ))
                }
              </div>
            </div>
            <div>
              <div className="ccf-settings-sub">Timing</div>
              <div className="ccf-seg" style={{ marginTop: 6 }}>
                <SegBtn active={discountTiming === 'pre-tax'}  onClick={() => setDiscount('timing', 'pre-tax')}>Pre-tax</SegBtn>
                <SegBtn active={discountTiming === 'post-tax'} onClick={() => setDiscount('timing', 'post-tax')}>Post-tax</SegBtn>
              </div>
            </div>
            {discountTiming === 'pre-tax' && (
              <ReactiveNote>Pre-tax reduces the taxable base — the customer pays less tax as a result.</ReactiveNote>
            )}
            {discountTiming === 'post-tax' && (
              <ReactiveNote>Post-tax applies after tax is calculated — the tax amount is unchanged, but the final amount the customer pays is reduced.</ReactiveNote>
            )}
          </>
        )}
      </div>

      {/* Billing period min/max */}
      <div className="ccf-settings-section">
        <div className="ccf-settings-label">Billing period min / max</div>
        <p className="ccf-field-caption">
          Checked at the end of every billing period. Below the min → upward adjustment added.
          Above the max → downward adjustment applied.
        </p>
        <div className="ccf-minmax-row">
          <div className="ccf-minmax-field">
            <span className="ccf-minmax-key">Min ({currency})</span>
            <input
              className="ccf-input-sm"
              type="number"
              min="0"
              placeholder="None"
              value={draft.bpMin}
              onChange={(e) => onChange('bpMin', e.target.value)}
            />
          </div>
          <div className="ccf-minmax-field">
            <span className="ccf-minmax-key">Max ({currency})</span>
            <input
              className="ccf-input-sm"
              type="number"
              min="0"
              placeholder="None"
              value={draft.bpMax}
              onChange={(e) => onChange('bpMax', e.target.value)}
            />
          </div>
        </div>
        {hasBpMin && !hasBpMax && (
          <ReactiveNote>Floor only — the customer pays at least {currency} {Number(draft.bpMin).toLocaleString()} each period, with no upper cap.</ReactiveNote>
        )}
        {hasBpMax && !hasBpMin && (
          <ReactiveNote>Ceiling only — invoices are capped at {currency} {Number(draft.bpMax).toLocaleString()}, with no minimum commitment.</ReactiveNote>
        )}
        {hasBpMin && hasBpMax && (
          <ReactiveNote>
            Every invoice lands between {currency} {Number(draft.bpMin).toLocaleString()} and {currency} {Number(draft.bpMax).toLocaleString()} — predictable for both sides.
          </ReactiveNote>
        )}
        {validFCs.length > 0 && (hasBpMin || hasBpMax) && (
          <>
            <div className="ccf-settings-sub" style={{ marginTop: 10 }}>Include in this check</div>
            <div className="ccf-fc-checklist ccf-fc-checklist--sm" style={{ marginTop: 6 }}>
              {validFCs.map((fc) => {
                const on = draft.bpIncluded.includes(fc);
                return (
                  <div key={fc}
                    className={`ccf-fc-check ccf-fc-check--sm${on ? ' ccf-fc-check--on' : ''}`}
                    onClick={() => onChange('bpIncluded', on
                      ? draft.bpIncluded.filter((f) => f !== fc)
                      : [...draft.bpIncluded, fc])}>
                    <div className="ccf-checkbox ccf-checkbox--sm">{on && '✓'}</div>
                    <span className="ccf-fc-check-name">{fc}</span>
                  </div>
                );
              })}
            </div>
            {partialBpIncl && (
              <ReactiveNote>
                Excluded components bill through unchanged — their amounts don't count toward
                the floor or ceiling. Useful for fixed fees you always want to pass through as-is.
              </ReactiveNote>
            )}
          </>
        )}
      </div>

      {/* Contract min/max — advanced */}
      {mode === 'advanced' && (
        <div className="ccf-settings-section">
          <div className="ccf-settings-label">Contract min / max (cumulative)</div>
          <p className="ccf-field-caption">
            Tracked across all billing periods for the life of the contract. Separate from
            the per-period check — both can be active at the same time.
          </p>
          <div className="ccf-minmax-row">
            <div className="ccf-minmax-field">
              <span className="ccf-minmax-key">Min ({currency})</span>
              <input
                className="ccf-input-sm"
                type="number"
                min="0"
                placeholder="None"
                value={draft.ctMin}
                onChange={(e) => onChange('ctMin', e.target.value)}
              />
            </div>
            <div className="ccf-minmax-field">
              <span className="ccf-minmax-key">Max ({currency})</span>
              <input
                className="ccf-input-sm"
                type="number"
                min="0"
                placeholder="None"
                value={draft.ctMax}
                onChange={(e) => onChange('ctMax', e.target.value)}
              />
            </div>
          </div>
          {hasCtMin && (
            <ReactiveNote>
              If the total spend across the contract falls short of {currency} {Number(draft.ctMin).toLocaleString()} at
              contract end, a single catch-up invoice makes up the difference.
            </ReactiveNote>
          )}
          {hasCtMax && (
            <ReactiveNote>
              Once cumulative spend hits {currency} {Number(draft.ctMax).toLocaleString()}, subsequent billing periods
              are zeroed out for the rest of the contract.
            </ReactiveNote>
          )}
        </div>
      )}

      {mode === 'advanced' && (
        <WhatIf>
          Try setting a billing period minimum. It guarantees a revenue floor on every invoice —
          even if the customer's usage comes in low that month.
        </WhatIf>
      )}
    </div>
  );
}

// ── Step 5: Summary ───────────────────────────────────────────────────────────

function StepSummary({ draft }) {
  const currency   = draft.currency;
  const profileDef = PROFILES.find((p) => p.id === draft.profile);

  return (
    <div className="ccf-step">
      <div className="ccf-step-head">
        <div className="ccf-step-eyebrow">Step 6 — Summary</div>
        <h2 className="ccf-step-title">Review before saving</h2>
        <StoryNote>
          Once saved as Draft, a billing admin sets the contract start date and activates it.
          From that point, the billing engine starts processing usage events and generating invoices.
        </StoryNote>
      </div>

      {profileDef && (
        <div className="ccf-review-section">
          <div className="ccf-review-group-label">Contract type</div>
          <div className="ccf-review-row">
            <span className="ccf-review-key">Type</span>
            <span className="ccf-review-val">{profileDef.icon} {profileDef.label}</span>
          </div>
          <div className="ccf-review-row">
            <span className="ccf-review-key">Billing cycle</span>
            <span className="ccf-review-val">{draft.billingCycle}</span>
          </div>
        </div>
      )}

      <div className="ccf-review-section">
        <div className="ccf-review-group-label">Customer</div>
        <div className="ccf-review-row">
          <span className="ccf-review-key">Name</span>
          <span className="ccf-review-val">{draft.customerName || '—'}</span>
        </div>
        {draft.customerTaxId && (
          <div className="ccf-review-row">
            <span className="ccf-review-key">Tax ID</span>
            <span className="ccf-review-val">{draft.customerTaxId}</span>
          </div>
        )}
        <div className="ccf-review-row">
          <span className="ccf-review-key">Currency</span>
          <span className="ccf-review-val">{currency}</span>
        </div>
      </div>

      <div className="ccf-review-section">
        <div className="ccf-review-group-label">Fees ({draft.feeComponents.length})</div>
        {draft.feeComponents.map((fc) => {
          const cfg = draft.fcConfigs[fc] || BLANK_FC_CONFIG;
          return (
            <div key={fc} className="ccf-review-fc">
              <div className="ccf-review-fc-name">{fc}</div>
              <div className="ccf-review-fc-pills">
                <span className={`ccf-review-pill ccf-review-pill--${cfg.paymentMode}`}>{cfg.paymentMode}</span>
                <span className="ccf-review-pill">{cfg.billingCycle}</span>
                {cfg.paymentMode === 'prepaid'  && cfg.trueUp              && <span className="ccf-review-pill ccf-review-pill--amber">true up</span>}
                {cfg.paymentMode === 'prepaid'  && cfg.trueDown             && <span className="ccf-review-pill ccf-review-pill--amber">true down</span>}
                {cfg.paymentMode === 'prepaid'  && cfg.autoAdjustEstimate   && <span className="ccf-review-pill">auto-adjust</span>}
                {cfg.paymentMode === 'postpaid' && cfg.accumulateUsage      && <span className="ccf-review-pill ccf-review-pill--amber">accumulate × {cfg.accumulationWindow}</span>}
                {cfg.scheduleMode !== 'contract' && (
                  <span className="ccf-review-pill">
                    {cfg.scheduleMode === 'cycle'
                      ? `Cycles ${cfg.scheduleFrom}–${cfg.scheduleTo || '∞'}`
                      : `${cfg.scheduleFrom} → ${cfg.scheduleTo || 'end'}`}
                  </span>
                )}
                {cfg.taxes.length > 0
                  ? cfg.taxes.map((t) => <span key={t} className="ccf-review-pill ccf-review-pill--tax">{t}</span>)
                  : <span className="ccf-review-pill ccf-review-pill--dim">No tax</span>}
                {cfg.discounts.length > 0 && (
                  <span className="ccf-review-pill ccf-review-pill--discount">
                    {cfg.discounts.length} discount band{cfg.discounts.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="ccf-review-section">
        <div className="ccf-review-group-label">Contract settings</div>
        {draft.virtualStartDate && (
          <div className="ccf-review-row">
            <span className="ccf-review-key">Virtual start</span>
            <span className="ccf-review-val">{draft.virtualStartDate}</span>
          </div>
        )}
        <div className="ccf-review-row">
          <span className="ccf-review-key">Auto-renew</span>
          <span className="ccf-review-val">{draft.autoRenew ? 'Enabled' : 'Disabled'}</span>
        </div>
        {draft.contractDiscount && (
          <div className="ccf-review-row">
            <span className="ccf-review-key">Contract discount</span>
            <span className="ccf-review-val">
              {draft.contractDiscount.type === 'percentage'
                ? `${draft.contractDiscount.value}%`
                : `${currency} ${draft.contractDiscount.value}`} · {draft.contractDiscount.timing}
            </span>
          </div>
        )}
        {(draft.bpMin || draft.bpMax) && (
          <div className="ccf-review-row">
            <span className="ccf-review-key">Period min / max</span>
            <span className="ccf-review-val">{fmtVal(draft.bpMin, currency)} – {fmtVal(draft.bpMax, currency)}</span>
          </div>
        )}
        {(draft.ctMin || draft.ctMax) && (
          <div className="ccf-review-row">
            <span className="ccf-review-key">Contract min / max</span>
            <span className="ccf-review-val">{fmtVal(draft.ctMin, currency)} – {fmtVal(draft.ctMax, currency)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Preview sidebar ───────────────────────────────────────────────────────────

function Preview({ draft, stepIdx }) {
  const currency   = draft.currency;
  const profileDef = PROFILES.find((p) => p.id === draft.profile);
  const title      = draft.customerName ? `${draft.customerName.split(' ')[0]}'s contract` : 'New contract';

  return (
    <aside className="ccf-preview">
      <div className="ccf-preview-head">{title}</div>

      {profileDef && (
        <div className="ccf-preview-block">
          <div className="ccf-preview-key">Contract type</div>
          <div className="ccf-preview-val ccf-preview-profile">
            <span className="ccf-preview-profile-icon">{profileDef.icon}</span>
            {profileDef.label}
          </div>
          <div className="ccf-preview-val ccf-preview-cycle">{draft.billingCycle}</div>
        </div>
      )}

      <div className="ccf-preview-block">
        <div className="ccf-preview-key">Customer</div>
        <div className={`ccf-preview-val${!draft.customerName ? ' ccf-preview-val--empty' : ''}`}>
          {draft.customerName || 'Not selected yet'}
        </div>
      </div>

      <div className="ccf-preview-block">
        <div className="ccf-preview-key">Currency</div>
        <div className="ccf-preview-val">{currency}</div>
      </div>

      {draft.feeComponents.length > 0 && (
        <div className="ccf-preview-block">
          <div className="ccf-preview-key">Fees</div>
          <div className="ccf-preview-chips">
            {draft.feeComponents.map((fc) => <span key={fc} className="ccf-preview-chip">{fc}</span>)}
          </div>
        </div>
      )}

      {stepIdx >= 3 && draft.feeComponents.length > 0 && (
        <div className="ccf-preview-block">
          <div className="ccf-preview-key">Configuration</div>
          {draft.feeComponents.map((fc) => {
            const cfg = draft.fcConfigs[fc] || BLANK_FC_CONFIG;
            return (
              <div key={fc} className="ccf-preview-fc-row">
                <span className="ccf-preview-fc-name">{fc}</span>
                <div className="ccf-preview-fc-pills">
                  <span className={`ccf-preview-mode ccf-preview-mode--${cfg.paymentMode}`}>{cfg.paymentMode}</span>
                  <span className="ccf-preview-cycle">{cfg.billingCycle}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {stepIdx >= 4 && (
        <div className="ccf-preview-block">
          <div className="ccf-preview-key">Guardrails</div>
          <div className="ccf-preview-setting">Auto-renew: {draft.autoRenew ? 'On' : 'Off'}</div>
          {draft.contractDiscount && (
            <div className="ccf-preview-setting">
              Discount: {draft.contractDiscount.type === 'percentage'
                ? `${draft.contractDiscount.value}%`
                : `${currency} ${draft.contractDiscount.value}`} ({draft.contractDiscount.timing})
            </div>
          )}
          {(draft.bpMin || draft.bpMax) && (
            <div className="ccf-preview-setting">
              Period: {fmtVal(draft.bpMin, currency)} – {fmtVal(draft.bpMax, currency)}
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

// ── Completion ────────────────────────────────────────────────────────────────

function Completion({ customerName, onReset, onNavigate }) {
  return (
    <div className="ccf-success">
      <div className="ccf-success-icon">✓</div>
      <h2 className="ccf-success-title">Contract saved as Draft</h2>
      <p className="ccf-success-sub">
        {customerName ? <><strong>{customerName}</strong>'s contract</> : 'This contract'} is now
        in <strong>Draft</strong> status. A billing admin sets the start date and activates it —
        at that point the billing engine starts processing usage events and generating invoices.
      </p>
      <div className="ccf-success-concepts">
        <div className="ccf-success-concept">Each fee can have a different billing cycle and payment mode</div>
        <div className="ccf-success-concept">Discounts are period-banded — type and value can change over time</div>
        <div className="ccf-success-concept">Min / max guardrails exist at both the billing period and contract level</div>
      </div>
      <div className="ccf-success-actions">
        <button className="ccf-btn ccf-btn--primary" onClick={onReset}>Build another contract</button>
        <button className="ccf-btn ccf-btn--ghost"   onClick={() => onNavigate('contract')}>Read the Contract docs</button>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ContractCreate({ onNavigate }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [draft,   setDraft]   = useState(buildDraft);
  const [done,    setDone]    = useState(false);
  const [mode,    setMode]    = useState('essentials');

  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  // Ensure fcConfigs entry exists whenever feeComponents list changes.
  // Use profile-aware defaults so payment mode is pre-set.
  const setFCs = (fcs) => {
    setDraft((d) => {
      const configs = { ...d.fcConfigs };
      fcs.forEach((f) => {
        if (!configs[f]) configs[f] = profileDefaultConfig(d.profile, d.billingCycle);
      });
      return { ...d, feeComponents: fcs, fcConfigs: configs };
    });
  };

  const canProceed = ([
    () => !!draft.profile,                         // step 0: profile must be chosen
    () => draft.customerName.trim().length > 0,    // step 1: customer
    () => draft.feeComponents.length > 0,          // step 2: at least 1 FC
    () => true,
    () => true,
    () => true,
  ][stepIdx] ?? (() => true))();

  const handleNext = () => {
    if (stepIdx < STEPS.length - 1) setStepIdx((i) => i + 1);
    else setDone(true);
  };

  if (done) {
    return (
      <div className="ccf">
        <Completion
          customerName={draft.customerName}
          onReset={() => { setDraft(buildDraft()); setStepIdx(0); setDone(false); }}
          onNavigate={onNavigate}
        />
      </div>
    );
  }

  return (
    <div className="ccf">
      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'customer',      label: 'Customer' },
          { id: 'fee', label: 'Fees' },
          { id: 'rate-card',     label: 'Rate Cards' },
          { id: 'contract',      label: 'Contracts' },
        ]}
      />
      <div className="ccf-topbar">
        <StepIndicator current={stepIdx} />
        <div className="ccf-mode-toggle">
          <span className="ccf-mode-label">Detail level</span>
          <div className="ccf-seg">
            <SegBtn active={mode === 'essentials'} onClick={() => setMode('essentials')}>Essentials</SegBtn>
            <SegBtn active={mode === 'advanced'}   onClick={() => setMode('advanced')}>Advanced</SegBtn>
          </div>
        </div>
      </div>

      <div className="ccf-body">
        <div className="ccf-main">
          {stepIdx === 0 && <StepIntent draft={draft} onChange={set} />}
          {stepIdx === 1 && <StepCustomer draft={draft} onChange={set} />}
          {stepIdx === 2 && (
            <StepFeeComponents
              feeComponents={draft.feeComponents}
              onChange={(key, val) => key === 'feeComponents' ? setFCs(val) : set(key, val)}
              profile={draft.profile}
            />
          )}
          {stepIdx === 3 && <StepConfigure draft={draft} onChange={set} mode={mode} profile={draft.profile} />}
          {stepIdx === 4 && <StepSettings  draft={draft} onChange={set} mode={mode} />}
          {stepIdx === 5 && <StepSummary   draft={draft} />}
        </div>
        <Preview draft={draft} stepIdx={stepIdx} />
      </div>

      <div className="ccf-footer">
        {stepIdx > 0
          ? <button className="ccf-btn ccf-btn--ghost" onClick={() => setStepIdx((i) => i - 1)}>← Back</button>
          : <span />
        }
        <button
          className={`ccf-btn${stepIdx === STEPS.length - 1 ? ' ccf-btn--finish' : ' ccf-btn--primary'}`}
          onClick={handleNext}
          disabled={!canProceed}
        >
          {stepIdx === STEPS.length - 1 ? 'Save as Draft →' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}
